import { Asset, AssetPrimaryMarket } from "~/modules/assets/types";
import { getCorpusAsset } from "~/modules/asset_chat/assets";
import { TRANSFERABILITY_TYPES } from "~/consts";
import { ScreenerAsset } from "../types";
import { getMeasureValue } from "~/utils/getMeasureValue";
import { addUtmTrackingToUrl } from "~/utils/addUtmTrackingToUrl";
import { getDealRoomConfig } from "./dealRoomConfig";
import { buildProductPreviewText } from "./productPreview";

// // // //

/**
 * An asset is "Non-KYC" when a no-KYC purchase path exists, which is decided
 * by the token's transfer permissioning: unrestricted or blacklist-only
 * tokens can reach any wallet (e.g. via a DEX), while whitelist-bound tokens
 * force every acquisition through someone's KYC. A blacklist only screens
 * sanctioned addresses — it doesn't gate purchase. Deliberately NOT based on
 * `primary_market__is_kyc_required`: platform-bound assets (e.g. Robinhood
 * stock tokens) report no subscription-step KYC because the platform already
 * KYC'd the buyer.
 */
function getIsNonKyc(asset: Pick<Asset, "transferability_types">): boolean {
    const transferability = asset.transferability_types ?? [];
    return (
        transferability.includes(TRANSFERABILITY_TYPES.WithoutRestrictions) ||
        transferability.includes(TRANSFERABILITY_TYPES.WithBlacklist)
    );
}

function normalizeSettlementTicker(
    raw: string | null | undefined,
): string | null {
    const trimmed = raw?.trim();
    return trimmed && trimmed.length > 0 ? trimmed : null;
}

function collectSubscriptionTickersFromPrimaryMarkets(
    markets: AssetPrimaryMarket[],
    options: { subscriptionAllowedOnly: boolean },
): string[] {
    const tickers = new Set<string>();

    for (const market of markets) {
        if (
            options.subscriptionAllowedOnly &&
            market.is_subscription_allowed === false
        ) {
            continue;
        }

        const t = normalizeSettlementTicker(market.base_asset_ticker);
        if (t) {
            tickers.add(t);
        }
    }

    return Array.from(tickers).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
}

/**
 * Assets investors may use to subscribe: primary-market `base_asset_ticker` values.
 * When several primary markets exist, each may use a different settlement asset (same idea as
 * the asset table "Base Assets" column). We prefer markets that allow subscription; if none
 * qualify, we fall back to all listed markets, then summary-row fields.
 */
function getSubscriptionAssetTickers(asset: Asset): string[] {
    const preferred = collectSubscriptionTickersFromPrimaryMarkets(
        asset.primary_markets ?? [],
        { subscriptionAllowedOnly: true },
    );
    if (preferred.length > 0) {
        return preferred;
    }

    const anyMarketTickers = collectSubscriptionTickersFromPrimaryMarkets(
        asset.primary_markets ?? [],
        { subscriptionAllowedOnly: false },
    );
    if (anyMarketTickers.length > 0) {
        return anyMarketTickers;
    }

    const fallback =
        normalizeSettlementTicker(asset.primary_market?.base_asset_ticker) ??
        normalizeSettlementTicker(asset.primary_market__base_asset_ticker) ??
        normalizeSettlementTicker(asset.denomination_asset_ticker) ??
        normalizeSettlementTicker(asset.pegged_asset?.ticker ?? null);

    return fallback ? [fallback] : [];
}

function getPrimaryMarket(asset: Asset) {
    if (asset.primary_markets?.length === 0) {
        return null;
    }

    if (asset.primary_markets?.length === 1) {
        return asset.primary_markets[0];
    }

    // Multiple markets - apply tiebreakers

    // 1. Prefer markets with minimum subscription amount
    const marketsWithMin = asset.primary_markets?.filter(
        (m) => m.subscription_minimum_amount !== null,
    );

    if (!marketsWithMin) {
        return null;
    }

    let candidates =
        marketsWithMin.length > 0
            ? marketsWithMin
            : (asset.primary_markets ?? []);

    if (candidates.length === 1) {
        return candidates[0];
    }

    // 2. Prefer markets with redemption frequency
    const marketsWithRedemption = candidates.filter(
        (m) => m.redemption_frequency !== null,
    );
    candidates =
        marketsWithRedemption.length > 0 ? marketsWithRedemption : candidates;

    if (candidates.length === 1) {
        return candidates[0];
    }

    // 3. Pick the lowest minimum subscription amount
    const candidatesWithMin = candidates.filter(
        (m) => m.subscription_minimum_amount !== null,
    );
    if (candidatesWithMin.length > 0) {
        return candidatesWithMin.reduce((min, m) =>
            m.subscription_minimum_amount < min.subscription_minimum_amount
                ? m
                : min,
        );
    }

    // Fallback to first candidate
    return candidates[0];
}

const LONG_REDEMPTION_FREQUENCY =
    "30 minutes if US markets are open. Otherwise next trading day.";

/**
 * The redemption frequency of an asset's primary market, with the one long
 * frequency split into a short label and a tooltip.
 */
function getRedemptionFrequency(primaryMarket: AssetPrimaryMarket | null): {
    label: string | null;
    tooltip: string | null;
} {
    const redemptionFrequency = primaryMarket?.redemption_frequency ?? null;

    if (redemptionFrequency === LONG_REDEMPTION_FREQUENCY) {
        return {
            label: "30 minutes",
            tooltip: "If US markets are open. Otherwise next trading day.",
        };
    }

    return { label: redemptionFrequency, tooltip: null };
}

function getServiceProviderName(
    provider: Asset["fund_administrator"],
): string | null {
    if (!provider) {
        return null;
    }
    return provider.legal_name || provider.name;
}

function getIncomeTreatment(asset: Asset): string | null {
    if (
        typeof asset.distributes_income !== "boolean" ||
        !asset.distribution_frequency_type
    ) {
        return null;
    }

    const action = asset.distributes_income ? "Distributes" : "Accumulates";
    return `${action} ${asset.distribution_frequency_type.toLowerCase()}`;
}

/**
 * getAssetItems
 * Converts an array of `Asset` objects to an array of objects to be displayed + filtered on the asset-screener page
 * @param assets
 * @returns
 */
export function getScreenerAssets(assets: Asset[]): ScreenerAsset[] {
    return assets.map((asset) => {
        // Get primary market
        const primaryMarket = getPrimaryMarket(asset);

        // Get APY values
        const apy30Day = getMeasureValue(asset, "apy_30_day");

        // Trailing 30-day APY — used for screener sort, filter, and card summary.
        const returnValue = apy30Day && apy30Day.val > 0 ? apy30Day.val : null;

        // Get AUM (bridged token value)
        const bridgedTokenValue = getMeasureValue(
            asset,
            "bridged_token_value_dollar",
        );
        const aumValue =
            bridgedTokenValue && bridgedTokenValue.val > 0
                ? bridgedTokenValue.val
                : null;

        const rawBridged = asset.bridged_token_value_dollar;
        const aum30dGrowthPct =
            rawBridged !== null &&
            typeof rawBridged.chg_30d_pct === "number" &&
            !Number.isNaN(rawBridged.chg_30d_pct)
                ? rawBridged.chg_30d_pct / 100
                : null;

        // Get holders count
        const holdersCountMeasure = getMeasureValue(
            asset,
            "holding_addresses_count",
        );
        const holdersCount =
            holdersCountMeasure && holdersCountMeasure.val > 0
                ? holdersCountMeasure.val
                : null;

        const managementFee =
            typeof asset.total_management_fee_bps === "number"
                ? asset.total_management_fee_bps
                : null;

        const performanceFee =
            typeof asset.total_performance_fee_bps === "number"
                ? asset.total_performance_fee_bps
                : null;

        const subscriptionFee =
            primaryMarket?.subscription_fee_bps ??
            asset.primary_market__subscription_fee_bps ??
            null;

        const redemptionFee =
            primaryMarket?.redemption_fee_bps ??
            asset.primary_market__redemption_fee_bps ??
            null;

        // Get minimum investment from primary market
        // ENHACNEMENT - should NOT fallback to USD, this should be removed
        const minInvestmentAmount =
            primaryMarket?.subscription_minimum_amount ?? null;
        const minInvestmentCurrency = primaryMarket?.base_asset_ticker || "USD";

        const redemptionFrequency = getRedemptionFrequency(primaryMarket);

        // Get redemption description
        const rawRedemptionDescription =
            primaryMarket?.redemption_description ??
            asset.primary_market__redemption_description ??
            null;
        const redemptionDescription = rawRedemptionDescription?.trim() || null;

        // Get investor eligibility
        const investorTypes = primaryMarket?.allowed_investor_types || [];

        // Get networks with colors and icons
        const networks = asset.tokens.map((token) => ({
            name: token.network.name,
            color: token.network.color_hex || "#6366f1",
            icon: token.network.icon_url,
        }));

        // Deduplicate networks by name
        const uniqueNetworks = networks.reduce(
            (acc, network) => {
                if (!acc.find((n) => n.name === network.name)) {
                    acc.push(network);
                }
                return acc;
            },
            [] as typeof networks,
        );

        // Get jurisdiction
        let jurisdictionName = "-";
        let jurisdictionIcon = null;

        if (asset.issuer && asset.issuer.legal_structure_country) {
            jurisdictionName = asset.issuer.legal_structure_country;
            jurisdictionIcon = asset.issuer.legal_structure_country_icon_url;
        }

        // Get inception date
        const inceptionDate = asset.inception_date
            ? new Date(asset.inception_date).toLocaleDateString("en-US", {
                  month: "numeric",
                  day: "numeric",
                  year: "numeric",
              })
            : "—";

        // Get platform from tokens[0]
        const token = asset.tokens[0];
        const platform = token?.platform
            ? {
                  id: token.platform.id,
                  slug: token.platform.slug,
                  name: token.platform.name,
                  icon_url: token.platform.icon_url,
              }
            : null;

        const manager = asset.sub_advisor
            ? {
                  name: asset.sub_advisor.legal_name || asset.sub_advisor.name,
                  icon_url: asset.sub_advisor.icon_url,
              }
            : asset.manager
              ? {
                    name: asset.manager.name,
                    icon_url: asset.manager.icon_url,
                }
              : asset.issuer
                ? {
                      name: asset.issuer.legal_name || asset.issuer.name,
                      icon_url: asset.issuer.icon_url,
                  }
                : null;

        const dealRoomConfig = getDealRoomConfig(asset);
        const dataRoomDocuments = dealRoomConfig?.documents ?? [];
        const symbol = asset.ticker || asset.slug || asset.id.toString();
        const corpusAsset = getCorpusAsset(symbol);
        const productPreview =
            (corpusAsset?.shortDescription ??
                dealRoomConfig?.previewBlurb ??
                buildProductPreviewText({
                    text: asset.description,
                    productNames: [asset.name, asset.ticker, asset.slug],
                })) ||
            null;

        // Map asset to AssetItem
        const assetItem: ScreenerAsset = {
            id: asset.id,
            asset_class: asset.asset_class,
            icon: asset.icon_url || null,
            website: asset.website ? addUtmTrackingToUrl(asset.website) : null,
            name: asset.name,
            symbol,
            platform: platform,
            manager,
            performanceManager: asset.manager
                ? {
                      id: asset.manager.id,
                      slug: asset.manager.slug,
                      name: asset.manager.name,
                      color_hex: asset.manager.color_hex,
                  }
                : null,
            serviceProviders: {
                administrator: getServiceProviderName(asset.fund_administrator),
                custodian:
                    getServiceProviderName(asset.traditional_custodian) ??
                    getServiceProviderName(asset.crypto_custodian),
                auditor: getServiceProviderName(asset.auditor),
                transferAgent: getServiceProviderName(asset.transfer_agent),
            },
            assetClass: asset.asset_class,
            sectors: (asset.sector_names as string[]) || [],
            regions: (asset.region_names as string[]) || [],
            fundStructure: asset.legal_structure__description ?? null,
            incomeTreatment: getIncomeTreatment(asset),
            subscriptionFrequency:
                primaryMarket?.subscription_frequency ??
                asset.primary_market__subscription_frequency ??
                null,
            description: asset.description || "",
            productPreview,
            dealBlurb:
                dealRoomConfig?.fullScreenBlurb ??
                dealRoomConfig?.blurb ??
                null,
            fullScreenBlurb: dealRoomConfig?.fullScreenBlurb ?? null,
            productSummarySections:
                dealRoomConfig?.productSummarySections ?? [],
            dataRoomDocuments,
            hasDataRoom: dataRoomDocuments.length > 0,
            isNonKyc: getIsNonKyc(asset),
            networks: uniqueNetworks,
            jurisdiction: {
                name: jurisdictionName,
                icon: jurisdictionIcon,
            },
            redemptionFrequency: {
                ...redemptionFrequency,
                description: redemptionDescription,
            },
            subscriptionAssets: getSubscriptionAssetTickers(asset),
            // // // //
            // Everything below this line is bullshit?
            // TODO - clean this up, flatten the stats object if possible
            stats: {
                return: returnValue,
                aum: aumValue,
                holdersCount: holdersCount,
                inception: inceptionDate,
                fee: managementFee,
                performanceFee,
                otherFees: asset.other_fees_description,
                subscriptionFee,
                redemptionFee,
                aum30dGrowthPct,
                investorTypes,
            },
            minInvestment: {
                amount: minInvestmentAmount,
                currency: minInvestmentCurrency,
            },
            returns: {
                trailing7Day: asset.trailing_7_day_return_percent ?? null,
                trailing1Month: asset.trailing_1_month_return_percent ?? null,
                trailing3Month: asset.trailing_3_month_return_percent ?? null,
                trailing1Year: asset.trailing_1_year_return_percent ?? null,
                yearToDate: asset.year_to_date_return_percent ?? null,
                sinceInception: asset.since_inception_return_percent ?? null,
            },
        };

        return assetItem;
    });
}
